/**
 * @bos/portal3 — THE ER PICTURE (phase 2): the data model as a diagram you can touch.
 *
 * A thin renderer over `erLayout.ts` — the geometry is decided there, this file only draws
 * it and turns clicks into the room's own gestures. Three laws govern what it is allowed
 * to be:
 *
 *   1. ONE PAYLOAD, TWO VIEWS. The Picture draws the same `RunDataModelResult.model` the
 *      Cards render — never `er-diagram.mmd`, never a second fetch. The two views cannot
 *      disagree because there is only one set of bytes under them.
 *   2. THE PICTURE IS A WAY TO REACH THE DOOR, NEVER A SECOND DOOR. Every gesture here
 *      compiles to exactly one typed `DataModelEdit` and opens the SAME composer the cards
 *      open, prefilled — same dry run, same basket, same all-or-nothing apply. Nothing on
 *      this canvas commits anything.
 *   3. NO AFFORDANCE FOR A DOOR THAT DOES NOT EXIST. The six `er-*` kinds are the whole
 *      vocabulary, so the canvas offers exactly six gestures. There is no "+ add a thing",
 *      no drag-a-field-to-connect, no enum-value editor: an edge is clickable only to reach
 *      its endpoints, and an enum badge is a fact, not a door. Drawing a control the
 *      machine would refuse is the one lie this product does not tell.
 *
 * ACCESSIBILITY. The canvas keeps ONE keyboard stop per entity (Tab to it, Enter or Space
 * to open it below) — the gesture targets inside a box are mouse shortcuts, each carrying a
 * `<title>` that names exactly what it will open, and every one of them is reachable
 * without a mouse through the composer in the panel beneath and through the Cards view,
 * which stays the equal-rights path and carries the dossier notes the Picture omits.
 */
import { useMemo } from 'react';
import type { DataModelEntity, DataModelLifecycle, DataModelRelationship } from '@bos/portal3-server';
import { TITLE_H, fieldRowBaselineY, layoutDataModel, type ErBox } from './erLayout';

/** What the canvas draws from — the served model's own shape, narrowed to what a picture needs. */
export interface CanvasModel {
  readonly entities: readonly DataModelEntity[];
  readonly relationships: readonly DataModelRelationship[];
  readonly lifecycles: readonly DataModelLifecycle[];
}

/**
 * The six gestures, one per existing edit kind. Present only while the door is open — a
 * closed door renders a picture with no controls at all, exactly as the cards do.
 */
export interface CanvasGestures {
  readonly onRenameField: (entity: string, field: string) => void;
  readonly onRetypeField: (entity: string, field: string) => void;
  readonly onAddField: (entity: string) => void;
  readonly onRemoveField: (entity: string, field: string) => void;
  readonly onRenameEntity: (entity: string) => void;
  readonly onRemoveEntity: (entity: string) => void;
}

export interface DataModelCanvasProps {
  readonly model: CanvasModel;
  /** The entity whose card is open beneath the canvas; null while nothing is selected. */
  readonly selected: string | null;
  readonly onSelect: (entity: string) => void;
  /** null = the door is closed: the picture is a read surface and offers nothing. */
  readonly gestures: CanvasGestures | null;
  /** Entity name → how many ORDERED-but-unapplied changes name it (never drawn as true). */
  readonly orderedCounts: ReadonlyMap<string, number>;
}

/** The width the type column gives up so a selected entity's rows can carry a remove mark. */
const TOOL_GUTTER = 15;

export function DataModelCanvas({ model, selected, onSelect, gestures, orderedCounts }: DataModelCanvasProps) {
  const layout = useMemo(() => layoutDataModel(model), [model]);

  /** ⚙ belongs ONLY to an enum whose lifecycle actually moves — a fixed list of labels is
   *  not a lifecycle, and marking it as one would send a person to a room with nothing in it. */
  const machines = useMemo(
    () => new Set(model.lifecycles.filter((l) => l.transitions.length > 0).map((l) => l.name)),
    [model.lifecycles],
  );

  const entityCount = model.entities.length;
  const edgeCount = layout.edges.length;

  return (
    <div className="er-canvas">
      <svg
        className="er-svg"
        width={layout.width}
        height={layout.height}
        viewBox={`0 0 ${layout.width} ${layout.height}`}
        role="img"
        aria-label={`Data model diagram: ${entityCount} ${entityCount === 1 ? 'entity' : 'entities'}, ${edgeCount} ${edgeCount === 1 ? 'connection' : 'connections'}`}
      >
        <defs>
          <marker
            id="er-arrow"
            viewBox="0 0 10 10"
            refX="9"
            refY="5"
            markerWidth="7"
            markerHeight="7"
            orient="auto-start-reverse"
          >
            <path d="M 0 1 L 9 5 L 0 9 z" className="er-arrow" />
          </marker>
        </defs>

        {/* Edges first — under the boxes. */}
        {layout.edges.map((e) => (
          <path
            key={`e${e.index}`}
            className={`er-edge${e.optional ? ' er-edge--optional' : ''}${selected !== null && (e.from === selected || e.to === selected) ? ' er-edge--selected' : ''}`}
            d={e.d}
            markerEnd="url(#er-arrow)"
          />
        ))}

        {/* Boxes. */}
        {layout.boxes.map((box) => (
          <EntityBox
            key={box.entity}
            box={box}
            entity={model.entities[box.index]}
            machines={machines}
            selected={selected === box.entity}
            onSelect={onSelect}
            gestures={gestures}
            ordered={orderedCounts.get(box.entity) ?? 0}
          />
        ))}

        {/* Labels last, each with a halo, so they stay readable over everything. */}
        {layout.labels.map((l, i) => (
          <g key={`l${i}`} className={`er-elabel${l.bus ? ' er-elabel--bus' : ''}`}>
            <text x={l.x} y={l.y} textAnchor="middle" className="er-elabel-halo" aria-hidden="true">
              {l.text}
            </text>
            <text x={l.x} y={l.y} textAnchor="middle" className="er-elabel-text">
              {l.text}
            </text>
          </g>
        ))}
      </svg>
    </div>
  );
}

interface EntityBoxProps {
  readonly box: ErBox;
  readonly entity: DataModelEntity;
  readonly machines: ReadonlySet<string>;
  readonly selected: boolean;
  readonly onSelect: (entity: string) => void;
  readonly gestures: CanvasGestures | null;
  readonly ordered: number;
}

function EntityBox({ box, entity, machines, selected, onSelect, gestures, ordered }: EntityBoxProps) {
  const name = entity.name;
  // The per-row remove mark and the two acts under the box belong to the entity a person
  // has actually opened — a canvas of 14 boxes each wearing its full toolkit is noise.
  const tools = gestures !== null && selected;
  const typeAnchorX = box.x + box.w - 10 - (tools ? TOOL_GUTTER : 0);

  /** A gesture takes the click for itself: the bubbled select must not undo the seeding. */
  const gesture = (run: () => void) => (event: { stopPropagation: () => void }) => {
    event.stopPropagation();
    run();
  };

  return (
    <g
      className={`er-entity${selected ? ' er-entity--selected' : ''}${ordered > 0 ? ' er-entity--ordered' : ''}`}
      data-entity={name}
      tabIndex={0}
      role="button"
      aria-label={`${name} — ${entity.fields.length} ${entity.fields.length === 1 ? 'field' : 'fields'}`}
      aria-current={selected ? 'true' : undefined}
      onClick={() => onSelect(name)}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          onSelect(name);
        }
      }}
    >
      <rect className="er-box" x={box.x} y={box.y} width={box.w} height={box.h} rx={10} />
      <rect className="er-titlebar" x={box.x} y={box.y} width={box.w} height={TITLE_H} rx={10} />
      <rect className="er-titlefix" x={box.x} y={box.y + TITLE_H - 10} width={box.w} height={10} />
      <text
        className={`er-title${gestures !== null ? ' er-act' : ''}`}
        x={box.x + 10}
        y={box.y + TITLE_H - 10}
        data-gesture={gestures !== null ? 'rename-entity' : undefined}
        onClick={gestures !== null ? gesture(() => gestures.onRenameEntity(name)) : undefined}
      >
        {name}
        {gestures !== null && <tspan className="er-pencil"> ✎</tspan>}
        {gestures !== null && <title>Rename {name}</title>}
      </text>
      {ordered > 0 && (
        <text className="er-ordered" textAnchor="end" x={box.x + box.w - 9} y={box.y + TITLE_H - 10}>
          {ordered} ordered
          <title>
            {ordered} change{ordered === 1 ? '' : 's'} ordered on this — yours, exactly as ordered,
            standing on every future run
          </title>
        </text>
      )}

      {entity.fields.map((f, i) => {
        const fy = fieldRowBaselineY(box, i);
        return (
          <g className="er-row" key={f.name}>
            <text
              className={`er-field${gestures !== null ? ' er-act' : ''}`}
              x={box.x + 10}
              y={fy}
              data-gesture={gestures !== null ? 'rename-field' : undefined}
              data-field={f.name}
              onClick={gestures !== null ? gesture(() => gestures.onRenameField(name, f.name)) : undefined}
            >
              {f.name}
              {f.name === entity.identityField && <tspan className="er-id"> ●</tspan>}
              {gestures !== null && (
                <title>
                  Rename {name}.{f.name}
                </title>
              )}
            </text>
            <text
              className={`er-type${gestures !== null ? ' er-act' : ''}`}
              textAnchor="end"
              x={typeAnchorX}
              y={fy}
              data-gesture={gestures !== null ? 'retype-field' : undefined}
              data-field={f.name}
              onClick={gestures !== null ? gesture(() => gestures.onRetypeField(name, f.name)) : undefined}
            >
              {f.type}
              {f.format !== undefined && <tspan className="er-fmt"> {f.format}</tspan>}
              {f.enumRef !== undefined && (
                <tspan className="er-enum">
                  {' '}
                  {machines.has(f.enumRef) ? '⚙ ' : ''}
                  {f.enumRef}
                </tspan>
              )}
              {gestures !== null && (
                <title>
                  Change the type of {name}.{f.name}
                </title>
              )}
            </text>
            {tools && (
              <text
                className="er-act er-remove"
                textAnchor="end"
                x={box.x + box.w - 8}
                y={fy}
                data-gesture="remove-field"
                data-field={f.name}
                onClick={gesture(() => gestures.onRemoveField(name, f.name))}
              >
                ✕
                <title>
                  Remove {name}.{f.name}
                </title>
              </text>
            )}
          </g>
        );
      })}

      {/* The two whole-entity acts, in the gap under the box a person has opened. */}
      {tools && (
        <>
          <text
            className="er-act er-boxact"
            x={box.x + 10}
            y={box.y + box.h + 13}
            data-gesture="add-field"
            onClick={gesture(() => gestures.onAddField(name))}
          >
            + field
            <title>Add a field to {name}</title>
          </text>
          <text
            className="er-act er-boxact"
            x={box.x + 66}
            y={box.y + box.h + 13}
            data-gesture="remove-entity"
            onClick={gesture(() => gestures.onRemoveEntity(name))}
          >
            ✕ remove
            <title>Remove {name} from the model</title>
          </text>
        </>
      )}
    </g>
  );
}
